import { Box, Stack } from "@mui/material";
import React, { useEffect, useState } from "react";
import { ListTableQueryPagination } from "../../components/ListTable/ListTableQueyPagination";
import { adminApiService } from "../../../utils/api";
import { GfUnit, MuiFilterRequestDto } from "@swagger/typescript-fetch-goflyer";
import { GridFilterModel, GridRowSelectionModel } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import { messages } from "../../../locales/messages";
import { TableData } from "../../../utils/interface/tableData";
import { AccountCardWrapper } from "../../components/AccountCardWrapper";
import { useUnitTableColumns } from "./useUnitTableColumns";
import {
  SetupPageContainer,
  SetupPageTopAppBar,
} from "../../../utils/commonStyledComponents/commonStyled";
import { TableWrapper } from "./styled";
import { LeftButtonSection } from "../CreateFlyerItemPage/styled";
import { BlackFontTransparentButton } from "../../../utils/commonStyledComponents/commonMuiStyled";
import DeleteIcon from "@mui/icons-material/Delete";
import { toastError, toastSuccess } from "../../../utils/pageUtil/toast";
import { MuiConfirmationModalUnit } from "../../components/MuiConfirmationModalUnit";
import { PermissionComponentWrapper } from "../../components/PermissionComponentWrapper";
import { useQueryParams } from "../../../hooks/useQueryParams";
import { muiFilterModelHasActiveItems } from "../../../utils/muiGridFilterModel";
import { useLatestRequestGuard } from "../../../hooks/useLatestRequestGuard";
import { guardedListFetch } from "../../../utils/guardedListFetch";
import { useAuthHeaders } from "../../../hooks/useAuthHeaders";

const UnitPage = () => {
  const { t } = useTranslation();
  const authHeaders = useAuthHeaders();

  const { paginationModel, sortModel, handleTableChange } = useQueryParams();

  const [paginationLoading, setPaginationLoading] = useState(false);
  const listRequestGuard = useLatestRequestGuard();
  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: [],
  });
  const [unitList, setUnitList] = useState<TableData<GfUnit>>();
  const [replacementUnits, setReplacementUnits] = useState<GfUnit[]>([]);
  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>([]);
  const [open, setOpen] = useState(false);
  const columns = useUnitTableColumns();

  const fetchUnitList = async () => {
    await guardedListFetch(
      listRequestGuard,
      setPaginationLoading,
      async () =>
        adminApiService.getManyBaseGfUnitAdminControllerGfUnit(
          undefined,
          undefined,
          undefined,
          undefined,
          sortModel?.length
            ? [`${sortModel[0].field},${sortModel[0]?.sort?.toUpperCase()}`]
            : [`updatedAt,DESC`],
          undefined,
          paginationModel?.pageSize,
          0,
          paginationModel?.page,
          undefined,
        ),
      (unitLists) => {
        setUnitList((prev) => ({
          ...prev,
          rows: unitLists.data,
          total: unitLists?.total,
        }));
      },
    );
  };

  const fetchUnitListWithFilter = async (
    activeFilterModel: GridFilterModel,
  ) => {
    await guardedListFetch(
      listRequestGuard,
      setPaginationLoading,
      async () => {
        const body: MuiFilterRequestDto = {
          filterModel: {
            items: activeFilterModel.items.map((item) => ({
              field: item.field,
              operator: item.operator,
              value: item.value,
              id: item.id,
            })),
            logicOperator: activeFilterModel.logicOperator as any,
            quickFilterValues: activeFilterModel.quickFilterValues,
            quickFilterLogicOperator:
              activeFilterModel.quickFilterLogicOperator as any,
          },
          page: paginationModel.page,
          pageSize: paginationModel.pageSize,
          sortModel: sortModel?.map((s) => ({
            field: s.field,
            sort: s.sort as any,
          })),
        };
        return adminApiService.gfUnitAdminControllerFilterWithMuiGrid(
          body,
          authHeaders,
        );
      },
      (response) => {
        if (!response) return;
        setUnitList((prev) => ({
          ...prev,
          rows: response.rows,
          total: response.total,
        }));
      },
    );
  };

  const handleFilterModelChange = (newFilterModel: GridFilterModel) => {
    setFilterModel(newFilterModel);
  };

  const fetchReplacementUnits = async () => {
    const result = await adminApiService.getManyBaseGfUnitAdminControllerGfUnit(
      undefined,
      undefined,
      undefined,
      undefined,
      [`name,ASC`],
    );
    setReplacementUnits(result.data);
  };

  const handleOpenDelete = () => {
    fetchReplacementUnits();
    setOpen(true);
  };

  const handleCancel = () => {
    setOpen(false);
  };

  const handleDeleteMerchant = async (replacementUnitId: string) => {
    if (rowSelectionModel?.length) {
      try {
        setPaginationLoading(true);
        const result = await rowSelectionModel?.map(async (value) => {
          return await adminApiService.gfUnitAdminControllerReplaceAndDelete(
            value as string,
            replacementUnitId,
          );
        });
        await Promise.all(result);

        if (muiFilterModelHasActiveItems(filterModel)) {
          fetchUnitListWithFilter(filterModel);
        } else {
          fetchUnitList();
        }

        setPaginationLoading(false);
        handleCancel();
        toastSuccess(t(messages.Successfully_Deleted()));
      } catch (error) {
        console.log("error", error);
        setPaginationLoading(false);
        toastError(
          t(messages.There_is_an_error_please_Contact_Administrator()),
        );
      }
    }
  };

  useEffect(() => {
    setUnitList({
      columns: columns,
    });
  }, []);

  useEffect(() => {
    const hasActiveFilters = muiFilterModelHasActiveItems(filterModel);
    if (hasActiveFilters) {
      fetchUnitListWithFilter(filterModel);
    } else {
      fetchUnitList();
    }
  }, [paginationModel, sortModel, filterModel]);

  return (
    <SetupPageContainer>
      <AccountCardWrapper title={t(messages.Unit()) as string}>
        <Box mt={2}>
          <SetupPageTopAppBar>
            <LeftButtonSection>
              <Stack direction="row" spacing={2}>
                <PermissionComponentWrapper
                  requiredPermission={["delete"]}
                  requiredPermissionGroup={["unit"]}
                >
                  <BlackFontTransparentButton
                    variant="text"
                    startIcon={
                      <DeleteIcon
                        style={{
                          color: "#ff8038",
                          fontSize: 25,
                          opacity: !rowSelectionModel?.length ? 0.5 : 1,
                        }}
                      />
                    }
                    disabled={!rowSelectionModel?.length}
                    onClick={handleOpenDelete}
                    data-testid="delete-button"
                  >
                    {t(messages.Delete())}
                  </BlackFontTransparentButton>
                </PermissionComponentWrapper>
              </Stack>
            </LeftButtonSection>
          </SetupPageTopAppBar>
          <TableWrapper>
            <ListTableQueryPagination
              loadFailed={listRequestGuard.loadFailed}
              columns={unitList?.columns}
              rows={unitList?.rows}
              total={paginationLoading ? 0 : unitList?.total}
              paginationModel={paginationModel}
              sortModel={sortModel}
              isLoading={paginationLoading}
              onTableChange={handleTableChange}
              handleRowSelection={(value) => {
                setRowSelectionModel(value);
              }}
              dynamicHeightAdjuster={230}
              filterModel={filterModel}
              onFilterModelChange={handleFilterModelChange}
            />
          </TableWrapper>
          <MuiConfirmationModalUnit
            open={open}
            unitList={replacementUnits.filter(
              (unit) => !rowSelectionModel.includes(unit.id),
            )}
            handleCancel={handleCancel}
            handleConfirm={handleDeleteMerchant}
            title={`${t(messages.Delete_Confirmation())}`}
            description={`
              ${t(messages.Are_you_sure_you_want_to_delete_These())} ${t(
              messages.Selected_Units(),
              { count: rowSelectionModel.length },
            )}?  ${t(messages.Please_select_a_replacement_unit())} ${t(
              messages.This_action_cannot_be_undone(),
            )}.
            `}
          />
        </Box>
      </AccountCardWrapper>
    </SetupPageContainer>
  );
};

export default UnitPage;
